<template>
  <div class="modern-master-view pb-12" v-if="item">
    <v-row class="mb-8 align-center px-4 pt-4">
      <div class="d-flex align-center">
        <v-btn icon class="mr-2" @click="$router.push({ name: 'CrmPipeline' })"><v-icon>fas fa-arrow-left</v-icon></v-btn>
        <div>
          <div class="text-h4 font-weight-black primary--text lh-1">{{ item.title }}</div>
          <div class="caption grey--text font-weight-bold uppercase tracking-widest mt-1">
            {{ item.account_name }} · {{ item.stage_label }}
          </div>
        </div>
      </div>
      <v-spacer></v-spacer>
      <v-btn
        outlined
        color="primary"
        class="font-weight-black text-uppercase tracking-widest rounded-lg px-6"
        :disabled="!item.customer_id"
        @click="createQuote"
      >
        Crear cotización
      </v-btn>
    </v-row>

    <v-row class="px-4">
      <v-col cols="12" md="7">
        <v-card outlined class="modern-card pa-6 mb-6">
          <div class="subtitle-2 font-weight-bold mb-4">Oportunidad</div>
          <v-text-field v-model="form.title" label="Título" outlined dense />
          <v-text-field v-model="form.account_name" label="Cuenta" outlined dense />
          <v-text-field v-model="form.contact_name" label="Contacto" outlined dense />
          <v-text-field v-model="form.phone" label="Teléfono" outlined dense />
          <v-text-field v-model="form.email" label="Email" outlined dense />
          <v-text-field v-model="form.amount" type="number" label="Monto estimado" outlined dense />
          <v-text-field v-model="form.next_action_at" type="date" label="Próximo contacto" outlined dense />
          <v-text-field v-model="form.next_action_note" label="Qué hay que hacer" outlined dense />
          <v-textarea v-model="form.notes" label="Notas" outlined dense rows="3" />
          <div class="caption error--text mb-2" v-if="!item.customer_id">
            Asocia un cliente Runflow para poder cotizar, o créalo acá con los datos básicos.
          </div>
          <v-autocomplete
            v-model="form.customer_id"
            :items="customerItems"
            :search-input.sync="customerSearch"
            item-text="name"
            item-value="id"
            label="Cliente Runflow"
            outlined
            dense
            clearable
            hide-no-data
          />
          <v-btn v-if="!item.customer_id" text small color="primary" class="px-0 mb-4" @click="openQuickCustomer">
            <v-icon left x-small>fas fa-plus</v-icon>
            Crear cliente para cotizar
          </v-btn>
          <v-btn color="primary" :loading="saving" @click="save">Guardar</v-btn>
          <quick-customer-dialog v-model="quickCustomerDialog" :prefill="quickPrefill" @created="onQuickCustomerCreated" />
        </v-card>

        <v-card outlined class="modern-card pa-6 mb-6">
          <div class="subtitle-2 font-weight-bold mb-3">Etapa</div>
          <v-chip-group>
            <v-chip
              v-for="stage in stages"
              :key="stage.value"
              :color="item.stage === stage.value ? 'primary' : ''"
              :outlined="item.stage !== stage.value"
              @click="moveStage(stage.value)"
            >
              {{ stage.text }}
            </v-chip>
          </v-chip-group>
          <v-text-field v-if="item.stage === 'lost'" v-model="form.lost_reason" label="Motivo de pérdida" outlined dense class="mt-4" />
        </v-card>
      </v-col>
      <v-col cols="12" md="5">
        <v-card outlined class="modern-card pa-6 mb-6">
          <div class="subtitle-2 font-weight-bold mb-3">Actividad</div>
          <v-select v-model="activity.tipo" :items="activityTypes" item-text="text" item-value="value" outlined dense label="Tipo" />
          <v-text-field v-model="activity.due_date" type="date" outlined dense label="Fecha" />
          <v-textarea v-model="activity.notes" outlined dense rows="2" label="Nota" />
          <v-btn color="primary" class="mb-6" :loading="savingActivity" @click="addActivity">Registrar</v-btn>
          <div v-for="row in item.activities || []" :key="row.id" class="mb-3 pa-3" style="border: 1px solid rgba(0,0,0,0.08); border-radius: 8px;">
            <div class="d-flex justify-space-between">
              <div class="font-weight-bold">{{ row.tipo_label }}</div>
              <v-btn v-if="!row.done" x-small text @click="complete(row)">Hecha</v-btn>
              <v-chip v-else x-small>Lista</v-chip>
            </div>
            <div class="caption grey--text">{{ row.due_date || 'Sin fecha' }} · {{ row.created_by_name }}</div>
            <div>{{ row.notes }}</div>
          </div>
        </v-card>
        <v-card outlined class="modern-card pa-6">
          <div class="subtitle-2 font-weight-bold mb-3">Cotizaciones ligadas</div>
          <div v-if="!(item.sales_notes || []).length" class="caption grey--text">Aún no hay cotización.</div>
          <v-list v-else dense>
            <v-list-item v-for="note in item.sales_notes" :key="note.id" :to="{ name: 'SalesNoteDetail', params: { sales_note: note.id } }">
              <v-list-item-content>
                <v-list-item-title>#{{ note.id }} {{ note.name }}</v-list-item-title>
                <v-list-item-subtitle>{{ note.status }}</v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
          </v-list>
        </v-card>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import notifications from '@/utils/notifications'
import { fetchCustomers } from '@/api/customers'
import QuickCustomerDialog from '@/components/customers/QuickCustomerDialog'
import {
  addCrmActivity,
  completeCrmActivity,
  getCrmOpportunity,
  setCrmOpportunityStage,
  updateCrmOpportunity,
} from '@/api/crm'

const STAGES = [
  { value: 'prospect', text: 'Prospecto' },
  { value: 'contacted', text: 'Contactado' },
  { value: 'quoting', text: 'Cotizando' },
  { value: 'negotiation', text: 'Negociación' },
  { value: 'won', text: 'Ganada' },
  { value: 'lost', text: 'Perdida' },
]

export default {
  name: 'CrmOpportunity',
  components: { QuickCustomerDialog },
  data() {
    return {
      item: null,
      saving: false,
      savingActivity: false,
      customerSearch: '',
      customerItems: [],
      quickCustomerDialog: false,
      quickPrefill: {},
      stages: STAGES,
      activityTypes: [
        { value: 'call', text: 'Llamado' },
        { value: 'visit', text: 'Visita' },
        { value: 'task', text: 'Tarea' },
        { value: 'other', text: 'Otro' },
      ],
      form: {},
      activity: { tipo: 'task', due_date: '', notes: '' },
    }
  },
  watch: {
    customerSearch(val) {
      if (val) this.searchCustomers(val)
    },
  },
  methods: {
    fillForm(item) {
      this.form = {
        title: item.title,
        account_name: item.account_name,
        contact_name: item.contact_name,
        phone: item.phone,
        email: item.email,
        amount: item.amount,
        next_action_at: item.next_action_at || '',
        next_action_note: item.next_action_note,
        notes: item.notes,
        customer_id: item.customer_id,
        lost_reason: item.lost_reason,
      }
      if (item.customer_id && item.customer_name) {
        this.customerItems = [{ id: item.customer_id, name: item.customer_name }]
      }
    },
    async fetchOne() {
      try {
        this.item = await getCrmOpportunity(this.$route.params.id)
        this.fillForm(this.item)
      } catch (e) {
        const msg = (e.response && e.response.data && (e.response.data.error || e.response.data.detail)) || 'No se pudo cargar la oportunidad'
        notifications.showError(msg)
      }
    },
    async searchCustomers(query) {
      try {
        const data = await fetchCustomers('', 1, 20, query)
        this.customerItems = data.results || []
      } catch (e) {
        this.customerItems = []
      }
    },
    async save() {
      this.saving = true
      try {
        const payload = {
          ...this.form,
          amount: this.form.amount === '' || this.form.amount == null ? 0 : this.form.amount,
          next_action_at: this.form.next_action_at || null,
          customer_id: this.form.customer_id || null,
        }
        this.item = await updateCrmOpportunity(this.item.id, payload)
        this.fillForm(this.item)
        notifications.showSuccess('Oportunidad guardada')
      } catch (e) {
        const msg = (e.response && e.response.data && e.response.data.error) || 'No se pudo guardar'
        notifications.showError(msg)
      } finally {
        this.saving = false
      }
    },
    async moveStage(stage) {
      try {
        this.item = await setCrmOpportunityStage(this.item.id, {
          stage,
          lost_reason: this.form.lost_reason,
        })
        this.fillForm(this.item)
      } catch (e) {
        notifications.showError('No se pudo cambiar la etapa')
      }
    },
    async addActivity() {
      this.savingActivity = true
      try {
        await addCrmActivity(this.item.id, this.activity)
        this.activity = { tipo: 'task', due_date: '', notes: '' }
        await this.fetchOne()
      } catch (e) {
        notifications.showError('No se pudo registrar la actividad')
      } finally {
        this.savingActivity = false
      }
    },
    async complete(row) {
      try {
        await completeCrmActivity(row.id)
        await this.fetchOne()
      } catch (e) {
        notifications.showError('No se pudo completar')
      }
    },
    openQuickCustomer() {
      this.quickPrefill = {
        name: this.form.account_name || this.item.account_name,
        phone: this.form.phone || this.item.phone,
        contact_name: this.form.contact_name || this.item.contact_name,
        email: this.form.email || this.item.email,
      }
      this.quickCustomerDialog = true
    },
    async onQuickCustomerCreated(customer) {
      if (!customer || !customer.id) return
      this.customerItems = [{ id: customer.id, name: customer.name }]
      this.form.customer_id = customer.id
      if (customer.name && !this.form.account_name) this.form.account_name = customer.name
      try {
        this.saving = true
        this.item = await updateCrmOpportunity(this.item.id, {
          ...this.form,
          customer_id: customer.id,
          amount: this.form.amount === '' || this.form.amount == null ? 0 : this.form.amount,
          next_action_at: this.form.next_action_at || null,
        })
        this.fillForm(this.item)
        notifications.showSuccess('Cliente asociado a la oportunidad')
      } catch (e) {
        notifications.showError('Cliente creado, pero no se pudo asociar. Guarda la oportunidad.')
      } finally {
        this.saving = false
      }
    },
    createQuote() {
      if (!this.item.customer_id) {
        notifications.showError('Asocia un cliente para cotizar')
        return
      }
      this.$router.push({
        name: 'SalesNoteCreateUpdate',
        query: { customer: this.item.customer_id, opportunity: this.item.id },
      })
    },
  },
  created() {
    this.fetchOne()
  },
}
</script>
